import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import MuiAlert from "@mui/material/Alert";
import ImageIcon from "@mui/icons-material/Image";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useDispatch, useSelector } from "react-redux";
import { createAxios } from "../../createAxios";
import { createSlider, uploadImage } from "../../redux/apiRequest";
import { Snackbar } from "@mui/material";
import AdminFormFrame from "../AdminFormFrame/AdminFormFrame";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    bgcolor: "var(--toeic-surface)",
    "& fieldset": { borderColor: "var(--toeic-line)", borderStyle: "dotted" },
    "&:hover fieldset": { borderColor: "var(--toeic-sky)" },
    "&.Mui-focused fieldset": { borderColor: "var(--toeic-sky)", borderWidth: 1.5 },
  },
  "& .MuiInputLabel-root": { fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--toeic-sky-deep)" },
};

const CreateSlider = () => {
  const [collection, setCollection] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [link, setLink] = useState("");
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [message, setMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.signin.currentUser);
  const accessToken = currentUser?.metadata.tokens.accessToken;
  const axiosJWT = createAxios(currentUser);

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const sliderData = {
      collection,
      content,
      image: "",
      link,
    };

    try {
      if (file) {
        setIsImageLoading(true);
        const imageData = await uploadImage(file, "", dispatch);

        if (imageData.img_url) {
          sliderData.image = imageData.img_url;
        }
        setIsImageLoading(false);
      }

      const result = await createSlider(
        accessToken,
        sliderData,
        dispatch,
        navigate,
        axiosJWT
      );

      if (result) {
        setMessage("Slider created successfully!");
        setSnackbarType("success");
        setOpenSnackbar(true);
      } else {
        setMessage(result.message || "Failed to create slider.");
        setSnackbarType("error");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setMessage("An error occurred while creating the slider.");
      setSnackbarType("error");
      setOpenSnackbar(true);
    }
  };


  return (
    <AdminFormFrame
      title="Create Slider"
      eyebrow="Homepage slider"
      description="Add a visual item to a weekly slider collection."
      icon={ImageIcon}
      color="#d81b60"
      bg="#ffe8f0"
      backHref="/management/collection"
      tips={["Collection should match the weekly group format.", "Keep content short enough for homepage display.", "Use a bright image with a valid destination link."]}
    >
      <form onSubmit={handleSubmit}>
        <Box mb={2}>
          <TextField
            fullWidth
            label="Collection"
            variant="outlined"
            value={collection}
            onChange={(e) => setCollection(e.target.value)}
            sx={fieldSx}
          />
        </Box>
        <Box mb={2}>
          <TextField
            fullWidth
            label="Content"
            variant="outlined"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            sx={fieldSx}
          />
        </Box>
        <Box mb={2}>
          <TextField
            fullWidth
            label="Link"
            variant="outlined"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            sx={fieldSx}
          />
        </Box>
        <Box mb={2}>
          <Button variant="outlined" component="label" fullWidth startIcon={<UploadFileIcon />} sx={{ borderRadius: "14px", borderStyle: "dotted", fontWeight: 700 }}>
            {isImageLoading ? (
              <>
                <CircularProgress style={{ color: "#FFFFFF" }} size={24} />
                &nbsp;Updating Image...
              </>
            ) : (
              "Upload Image"
            )}
            <input type="file" hidden onChange={handleFileChange} />
          </Button>
        </Box>
        <Box mb={2}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ borderRadius: "14px", bgcolor: "#35c759", color: "#ffffff", fontWeight: 700, "&:hover": { bgcolor: "#2fb44f" } }}
          >
            Submit
          </Button>
        </Box>
      </form>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbarType}
          sx={{ width: "100%", borderRadius: "14px", fontWeight: 800, boxShadow: "var(--toeic-shadow-card)" }}
        >
          {message}
        </MuiAlert>
      </Snackbar>
    </AdminFormFrame>
  );
};

export default CreateSlider;



